import { buildClient } from "@datocms/cma-client-browser";
import { useSearchParams } from "next/navigation";
import React from "react";
import { useSiteSearch as useOriginalSiteSearch } from "react-datocms/use-site-search";
import { useSiteSettings } from "src/lib/hooks";

const client = buildClient({
	apiToken: process.env.DATOCMS_SITE_SEARCH_TOKEN ?? null,
});

const useDatoSiteSearch = (resultsPerPage: number) => {
	const {
		locale,
		brandConfig: { buildTriggerId },
	} = useSiteSettings();
	const searchParams = useSearchParams();
	const searchTerm = searchParams.get("searchTerm");

	const siteSearch = useOriginalSiteSearch({
		client,
		buildTriggerId: buildTriggerId ?? "37830", // let's use the production build trigger (de locale) as fallback
		initialState: { locale, query: searchTerm ?? "" },
		highlightMatch: (text, key, context) => {
			if (context === "title") return text;

			return <strong key={key}>{text}</strong>;
		},
		resultsPerPage,
		fuzzySearch: true,
	});

	return siteSearch;
};

const useSiteSearch = (resultsPerPage = 5) => {
	const [showSearch, setShowSearch] = React.useState(false);
	const closeSearch = () => setShowSearch(false);
	const toggleSearch = () => setShowSearch((prev) => !prev);
	const siteSearch = useDatoSiteSearch(resultsPerPage);

	const { commonSearchTerms } = useSiteSettings();

	const isLoading = !siteSearch.data && !siteSearch.error;
	const isError = Boolean(siteSearch.error);
	const isEmptyInput = !siteSearch.state.query.length;
	const isEmptyResult =
		!isLoading && !isError && siteSearch.data?.pageResults.length === 0;

	return {
		...siteSearch,
		showSearch,
		closeSearch,
		toggleSearch,
		isLoading,
		isError,
		isEmptyInput,
		isEmptyResult,
		commonSearchTerms,
	};
};

const SearchContext = React.createContext<ReturnType<
	typeof useSiteSearch
> | null>(null);

export const SearchProvider: React.FC<
	React.PropsWithChildren & { resultsPerPage?: number }
> = ({ children, resultsPerPage }) => {
	const siteSearch = useSiteSearch(resultsPerPage);

	return (
		<SearchContext.Provider value={siteSearch}>
			{children}
		</SearchContext.Provider>
	);
};

export const useSearchContext = () => {
	const context = React.useContext(SearchContext);
	if (!context) {
		throw new Error(
			"useSearchContext must be used within a SearchProvider",
		);
	}
	return context;
};

// 🔬 TBD
